import { useMemo } from 'react';

import type { ColGroupDef } from 'ag-grid-enterprise';

import type { SuppressionDetail } from '@enterprise/api';

import type { ListSegmentCellProps } from '@components/grid/renderers/types/list-segment-renderer.params';

import DateCellRenderer from '@components/grid/renderers/date-cell.renderer';
import ListSegmentRenderer from '@components/grid/renderers/list-segment-cell-renderer';
import NumberCellRenderer from '@components/grid/renderers/number-cell.renderer';

export const useGetSuppressionDetailColumns = () => {
  return useMemo(() => {
    return [
      {
        children: [
          {
            field: 'mailingId',
            headerName: 'Mailing ID',
            hide: true,
            width: 125,
          },
          {
            cellDataType: 'date',
            cellRenderer: DateCellRenderer,
            field: 'mailDate',
            headerName: 'Mail Date',
            width: 150,
          },
          {
            field: 'mailCode',
            headerName: 'Mail Code',
            width: 150,
          },
          {
            cellRenderer: ListSegmentRenderer,
            cellRendererParams: (params: ListSegmentCellProps<SuppressionDetail>) => {
              return {
                ...params,
                header: 'listDisplayName',
                subHeader: 'listManagerName',
              } as ListSegmentCellProps<SuppressionDetail>;
            },
            field: 'listDisplayName',
            headerName: 'List/Segment',
            wrapText: true,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            field: 'quantityOrdered',
            headerName: 'Requested',
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellRenderer: NumberCellRenderer,
            field: 'quantityReceived',
            headerName: 'Received',
            width: 150,
          },
        ],
        headerClass: ['bg-blue-950'],
        headerName: 'Mailing and List Information',
      },
      {
        children: [
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'stateOmits',
            headerClass: ['bg-yellow-800'],
            headerName: 'State Omit',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'badRecords',
            headerClass: ['bg-yellow-800'],
            headerName: 'Bad Recs',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'client',
            headerClass: ['bg-yellow-800'],
            headerName: 'Client',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'agency',
            headerClass: ['bg-yellow-800'],
            headerName: 'Agency',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'dma',
            headerClass: ['bg-yellow-800'],
            headerName: 'DMA',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'prison',
            headerClass: ['bg-yellow-800'],
            headerName: 'Prison',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'recency',
            headerClass: ['bg-yellow-800'],
            headerName: 'Recency',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'esun',
            headerClass: ['bg-yellow-800'],
            headerName: 'eSUN',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'vacancies',
            headerClass: ['bg-yellow-800'],
            headerName: 'Vacancies',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'zipOmits',
            headerClass: ['bg-yellow-800'],
            headerName: 'ZIP Omit',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'scfOmits',
            headerClass: ['bg-yellow-800'],
            headerName: 'SCF Omit',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-yellow-200', 'text-yellow-900'],
            cellRenderer: NumberCellRenderer,
            field: 'districts',
            headerClass: ['bg-yellow-800'],
            headerName: 'Districts',
            type: ['numericColumn'],
            width: 150,
          },
          {
            aggFunc: 'sum',
            cellClass: ['bg-green-200', 'text-green-900'],
            cellRenderer: NumberCellRenderer,
            field: '',
            headerClass: ['bg-yellow-800'],
            headerName: 'Total',
            valueGetter: ({ data }) => {
              const {
                agency = 0,
                badRecords = 0,
                client = 0,
                districts = 0,
                dma = 0,
                esun = 0,
                prison = 0,
                recency = 0,
                scfOmits = 0,
                stateOmits = 0,
                vacancies = 0,
                zipOmits = 0,
              } = data ?? {};

              return (
                agency +
                badRecords +
                client +
                dma +
                esun +
                prison +
                recency +
                stateOmits +
                scfOmits +
                zipOmits +
                districts +
                vacancies
              );
            },
            width: 175,
          },
        ],
        headerClass: ['bg-yellow-900'],
        headerGroupComponentParams: {
          icon: 'fa-user-minus',
        },
        headerName: 'Suppressions',
      },
    ] as ColGroupDef<SuppressionDetail>[];
  }, []);
};
